import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Card } from "@/components/Card";
import { Btn } from "@/components/Btn";
import { SectionHeader } from "@/components/SectionHeader";
import { SettingsRow, S_INPUT } from "@/components/SettingsRow";
import { ApiError } from "@/api/client";
import {
  CiPreviewVariante,
  SettingsConfig,
  WEBSICHERE_FONTS,
  getCiDesignPreview,
  getSettingsConfig,
  putSettingsConfig,
} from "@/api/settings";

type Props = { isAdmin: boolean };

type CiCfg = Pick<
  SettingsConfig,
  | "CI_NAME"
  | "CI_PRIMAERFARBE"
  | "CI_AKZENTFARBE"
  | "CI_ADRESSE"
  | "CI_TELEFON"
  | "CI_EMAIL"
  | "CI_WEBSITE"
  | "CI_FONT_UEBERSCHRIFT"
  | "CI_FONT_TEXT"
>;

const EMPTY: CiCfg = {
  CI_NAME: "",
  CI_PRIMAERFARBE: "#6b4f44",
  CI_AKZENTFARBE: "#b7765b",
  CI_ADRESSE: "",
  CI_TELEFON: "",
  CI_EMAIL: "",
  CI_WEBSITE: "",
  CI_FONT_UEBERSCHRIFT: "Georgia",
  CI_FONT_TEXT: "Georgia",
};

const CI_KEYS = Object.keys(EMPTY) as (keyof CiCfg)[];

const VARIANTEN: { id: CiPreviewVariante; label: string }[] = [
  { id: "buchungsbestaetigung", label: "Buchungsbestätigung" },
  { id: "infos_gruppe", label: "Infos an Gruppe" },
  { id: "rechnung", label: "Rechnung" },
];

function pick(data: SettingsConfig): CiCfg {
  return CI_KEYS.reduce((acc, k) => ({ ...acc, [k]: data[k] }), {} as CiCfg);
}

function ColorField({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
      <input
        type="color"
        value={/^#[0-9a-fA-F]{6}$/.test(value) ? value : "#6b4f44"}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        style={{
          width: 38,
          height: 34,
          padding: 0,
          border: "1px solid var(--border-strong)",
          borderRadius: 8,
          background: "var(--bg-card)",
          cursor: disabled ? "default" : "pointer",
        }}
      />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        style={{ ...S_INPUT, maxWidth: 130, fontFamily: "var(--font-mono)" }}
      />
    </div>
  );
}

export function CiDesignSection({ isAdmin }: Props) {
  const qc = useQueryClient();
  const configQ = useQuery({ queryKey: ["settings-config"], queryFn: getSettingsConfig });

  const [cfg, setCfg] = useState<CiCfg>(EMPTY);
  const [dirty, setDirty] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [variante, setVariante] = useState<CiPreviewVariante>("buchungsbestaetigung");

  useEffect(() => {
    if (configQ.data) {
      setCfg(pick(configQ.data));
      setDirty(false);
    }
  }, [configQ.data]);

  // Vorschau rendert das echte Backend-Template mit den aktuellen (ggf. ungespeicherten) Werten.
  const previewQ = useQuery({
    queryKey: ["ci-preview", variante, cfg],
    queryFn: () => getCiDesignPreview({ variante, ...cfg }),
    enabled: !!configQ.data,
  });

  const previewSrc = useMemo(() => previewQ.data ?? "", [previewQ.data]);

  const saveMut = useMutation({
    mutationFn: () => putSettingsConfig(cfg),
    onSuccess: async (next) => {
      setCfg(pick(next));
      setDirty(false);
      setMsg("Gespeichert.");
      await qc.invalidateQueries({ queryKey: ["settings-config"] });
    },
    onError: (e: unknown) => {
      setMsg(e instanceof ApiError ? e.message : "Speichern fehlgeschlagen.");
    },
  });

  function set<K extends keyof CiCfg>(key: K, val: string) {
    setCfg((prev) => ({ ...prev, [key]: val }));
    setDirty(true);
    setMsg(null);
  }

  return (
    <>
      {!isAdmin && (
        <Card style={{ marginBottom: 16, background: "var(--bg-subtle)" }}>
          <div style={{ fontSize: 12, color: "var(--fg-muted)" }}>
            <span className="material-icons-outlined" style={{ fontSize: 16, verticalAlign: "-3px" }}>
              info
            </span>{" "}
            Nur lesbar — Änderungen können nur Administratoren speichern.
          </div>
        </Card>
      )}

      <Card style={{ marginBottom: 16 }}>
        <SectionHeader
          title="E-Mail-Design (CI)"
          desc="Diese Angaben erscheinen im Kopf und Fuß jeder versendeten E-Mail (HTML). Der Platzhalter {buchungsuebersicht} in den E-Mail-Vorlagen zeigt die strukturierte Buchungs-Infobox."
        />
        <SettingsRow label="Name" hint="Erscheint im Header neben dem Logo und im Footer.">
          <input
            type="text"
            value={cfg.CI_NAME}
            onChange={(e) => set("CI_NAME", e.target.value)}
            disabled={!isAdmin}
            style={S_INPUT}
            placeholder="Pfadfinderzentrum Raumünzach"
          />
        </SettingsRow>
        <SettingsRow label="Primärfarbe" hint="Hintergrund der Kopfleiste.">
          <ColorField value={cfg.CI_PRIMAERFARBE} onChange={(v) => set("CI_PRIMAERFARBE", v)} disabled={!isAdmin} />
        </SettingsRow>
        <SettingsRow label="Akzentfarbe" hint="Kopfzeile der Buchungs-Infobox.">
          <ColorField value={cfg.CI_AKZENTFARBE} onChange={(v) => set("CI_AKZENTFARBE", v)} disabled={!isAdmin} />
        </SettingsRow>
        <SettingsRow label="Schrift Überschrift" hint="Header/Name. Nur websichere Schriften (Outlook-tauglich).">
          <select
            value={cfg.CI_FONT_UEBERSCHRIFT}
            onChange={(e) => set("CI_FONT_UEBERSCHRIFT", e.target.value)}
            disabled={!isAdmin}
            style={{ ...S_INPUT, maxWidth: 220 }}
          >
            {WEBSICHERE_FONTS.map((f) => (
              <option key={f} value={f} style={{ fontFamily: f }}>
                {f}
              </option>
            ))}
          </select>
        </SettingsRow>
        <SettingsRow label="Schrift Text" hint="Fließtext und Buchungs-Infobox – dieselbe Schrift verhindert Outlook-Schriftmix.">
          <select
            value={cfg.CI_FONT_TEXT}
            onChange={(e) => set("CI_FONT_TEXT", e.target.value)}
            disabled={!isAdmin}
            style={{ ...S_INPUT, maxWidth: 220 }}
          >
            {WEBSICHERE_FONTS.map((f) => (
              <option key={f} value={f} style={{ fontFamily: f }}>
                {f}
              </option>
            ))}
          </select>
        </SettingsRow>
        <SettingsRow label="Adresse" hint="Footer, z. B. Straße, PLZ und Ort.">
          <input
            type="text"
            value={cfg.CI_ADRESSE}
            onChange={(e) => set("CI_ADRESSE", e.target.value)}
            disabled={!isAdmin}
            style={S_INPUT}
          />
        </SettingsRow>
        <SettingsRow label="Telefon">
          <input
            type="text"
            value={cfg.CI_TELEFON}
            onChange={(e) => set("CI_TELEFON", e.target.value)}
            disabled={!isAdmin}
            style={S_INPUT}
          />
        </SettingsRow>
        <SettingsRow label="Kontakt-E-Mail">
          <input
            type="text"
            value={cfg.CI_EMAIL}
            onChange={(e) => set("CI_EMAIL", e.target.value)}
            disabled={!isAdmin}
            style={S_INPUT}
          />
        </SettingsRow>
        <SettingsRow label="Website">
          <input
            type="text"
            value={cfg.CI_WEBSITE}
            onChange={(e) => set("CI_WEBSITE", e.target.value)}
            disabled={!isAdmin}
            style={S_INPUT}
            placeholder="www.pfadfinderzentrum-raumuenzach.de"
          />
        </SettingsRow>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
          {msg && (
            <span style={{ fontSize: 12, color: "var(--fg-muted)", alignSelf: "center" }}>{msg}</span>
          )}
          <Btn
            variant="primary"
            icon="check"
            onClick={() => saveMut.mutate()}
            disabled={!isAdmin || !dirty || saveMut.isPending}
          >
            Speichern
          </Btn>
        </div>
      </Card>

      <Card>
        <SectionHeader
          title="Vorschau"
          desc="So sieht die versendete E-Mail mit den aktuellen Werten aus (echtes Mail-Template)."
        />
        <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
          {VARIANTEN.map((v) => (
            <button
              key={v.id}
              type="button"
              onClick={() => setVariante(v.id)}
              style={{
                padding: "6px 12px",
                borderRadius: 8,
                border: "1px solid var(--border-strong)",
                background: variante === v.id ? "var(--brand-100)" : "var(--bg-card)",
                color: variante === v.id ? "var(--brand-900)" : "var(--fg-muted)",
                fontSize: 13,
                fontFamily: "var(--font-body)",
                fontWeight: variante === v.id ? 600 : 400,
                cursor: "pointer",
              }}
            >
              {v.label}
            </button>
          ))}
        </div>
        <div
          style={{
            border: "1px solid var(--border)",
            borderRadius: 10,
            overflow: "hidden",
            background: "#f6f4f1",
            minHeight: 480,
            position: "relative",
          }}
        >
          {previewQ.isError ? (
            <div style={{ padding: 20, fontSize: 13, color: "var(--fg-muted)" }}>
              Vorschau konnte nicht geladen werden.
            </div>
          ) : (
            <iframe
              title="E-Mail-Vorschau"
              srcDoc={previewSrc}
              sandbox=""
              style={{ width: "100%", height: 560, border: "none", display: "block", background: "#f6f4f1" }}
            />
          )}
        </div>
      </Card>
    </>
  );
}
